/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-console */
import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { MatSnackBar } from '@angular/material/snack-bar';
import { Action, Store } from '@ngrx/store';
import { IAuthenticatedUser } from './user.entity';
import {
  Observable,
  throwError,
  of,
  from,
  map,
  switchMap,
  filter,
  tap,
  mergeMap,
  catchError,
  distinctUntilChanged,
  take,
  withLatestFrom,
  mapTo,
} from 'rxjs';
import { warnOnErrorWithRetryOption$ } from '../services/project/project-detail.effects';
import {
  selectAccountManagementStatus,
  selectAuthenticatedUser,
  selectAuthenticatedUserId,
  selectManagementToken,
} from './auth.selectors';
import { Router } from '@angular/router';
import { IUser } from '@core/auth/user.entity';
import { normalize } from 'normalizr';
import { IdToken } from '@auth0/auth0-angular';
import { createAuth0Client, User } from '@auth0/auth0-spa-js';
import { AppConfigService } from '@core/services/config/app-config.service';
import { HttpClient } from '@angular/common/http';
import {
  investigatorSchema,
  investigatorUsageDtoSchema,
} from '@core/state/entities/investigator/investigator.entity';
import {
  AccountHttpService,
  ApplicationHttpService,
  ProfileUpdatedDto,
  UpdateEmailErrorResponse,
  VerificationErrorResponse,
} from '@core/services/api-client.generated';
import {
  AUTH_ALERT_COOKIE_KEY,
  COOKIE_ALERT_COOKIE_KEY,
  coreActions,
} from '@core/actions/core.actions';
import { CookieService } from 'ngx-cookie-service';
import { authApiActions } from './auth.actions';
import { FullNameClass } from '@shared/pipes/full-name/full-name.model';
import { throwErrorExp } from '@core/actions/util';
import { projectSchema } from '@core/state/entities/project/project.entity';
import { stageSchema } from '@core/state/entities/stage/stage.entity';
import { v4 as uuid } from 'uuid';
import { AUTH_PROVIDER } from './auth-provider.token';
import { IAuthProvider } from './auth-provider.interface';

export const syrfClaimsNamespace = 'https://claims.syrf.org.uk';
export const createUserFromClaims = ({
                                       email,
                                       family_name,
                                       nickname,
                                       given_name,
                                       picture,
                                       sub,
                                       [`${syrfClaimsNamespace}/syrf_groups`]: syrf_groups,
                                       [`${syrfClaimsNamespace}/user_id`]: id,
                                     }: any): IAuthenticatedUser => ({
  email,
  pictureUrl: picture,
  fullName: new FullNameClass(
    given_name,
    family_name,
    nickname,
  ).asSerializable(),
  id,
  sub,
  syrf_groups: (syrf_groups && [syrf_groups.split(' ')].flat()) || [],
  // Linked identities are deliberately not a token claim. The authenticated
  // native account-management endpoint populates this projection after login.
  identities: [],
  // In Auth0 mode, "fully authenticated" means the user has a native Auth0 account
  // (not social-only like google-oauth2|xxx). In BFF/mock-OIDC mode, all users are
  // fully authenticated since they went through the complete login flow.
  fullAuth0: sub.startsWith('auth0') || !sub.includes('|') || sub.startsWith('system|'),
});
export const updatedAuth0User = (
  { auth0User }: ProfileUpdatedDto,
  oldUser: IAuthenticatedUser,
): IAuthenticatedUser => ({
  ...oldUser,
  ...auth0User,
  // Preserve the original sub from the identity token; the profile-update DTO
  // sets Sub to the internal investigator GUID, which would break Auth0
  // Management API calls that depend on the auth0|xxx subject format.
  sub: oldUser.sub,
});
const localStorageImpersonationKeyPrefix = 'syrf-impersonation-';

export interface IStoredImpersonationInfo {
  impersonatedUser: IUser;
  authenticatedUserId: string;
}

export const profileUpdatedDtoSchema = {
  investigator: investigatorSchema,
};
export const investigatorInitialInfoDtoSchema = {
  investigatorUsage: investigatorUsageDtoSchema,
  projects: [projectSchema],
  stages: [stageSchema],
  investigator: investigatorSchema,
};

@Injectable()
export class AuthEffects {
  // idTokenReceived fires again on every silent token refresh, not only at
  // login. Reload only when the projection is not already in hand, so a
  // refresh does not re-hit the endpoint (and, in Auth0 mode, the Auth0
  // Management API) once per token. `error` is retried; the reducer resets
  // the status to `idle` whenever the subject changes, so a genuinely new
  // account always loads. The account page dispatches loadAccountManagement
  // directly on init, so an explicit refresh is still unconditional.
  loadAccountManagementAfterLogin$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.idTokenReceived),
      concatLatestFrom(() => this._store.select(selectAccountManagementStatus)),
      filter(([, status]) => status === 'idle' || status === 'error'),
      map(() => authApiActions.loadAccountManagement()),
    );
  });

  loadAccountManagement$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.loadAccountManagement),
      switchMap(() =>
        this._accountService.getAccountManagement().pipe(
          map((account) => authApiActions.accountManagementReceived(account)),
          catchError(() => of(authApiActions.accountManagementFailed())),
        ),
      ),
    );
  });

  accountDeactivated$ = createEffect(() =>
    this._actions$.pipe(
      ofType(authApiActions.deactivateAccount),
      switchMap((a) =>
        this._accountService.deleteProfile(false).pipe(
          mergeMap((_) => [
            authApiActions.signOut({ onlyLocal: false }),
            authApiActions.accountDeactivated(),
          ]),
          catchError((err) => of(authApiActions.accountDeactivationFailed())),
        ),
      ),
    ),
  );

  accountDeleted$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.deleteAccount),
      switchMap((a) =>
        this._accountService.deleteProfile(true).pipe(
          mergeMap((_) => [
            authApiActions.signOut({ onlyLocal: false }),
            authApiActions.accountDeleted(),
          ]),
          catchError((err) => of(authApiActions.accountDeletionFailed())),
        ),
      ),
    );
  });

  authAlertCookieUpdate$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(coreActions.setAuthAlertDisplayStatus),
        tap((props) =>
          this._cookieService.set(AUTH_ALERT_COOKIE_KEY, props.status),
        ),
      );
    },
    { dispatch: false },
  );

  cookieAlertCookieUpdate$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(coreActions.setCookieAlertDisplayStatus),
        tap((props) =>
          this._cookieService.set(COOKIE_ALERT_COOKIE_KEY, props.status),
        ),
      );
    },
    { dispatch: false },
  );
  profileAssociatedEmailRemoved$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.removeEmailFromProfile),
      switchMap((props) =>
        this._accountService.deleteProfileEmail(props.email).pipe(
          map((profileUpdatedDto) =>
            authApiActions.emailRemovedFromProfile({
              ...profileUpdatedDto,
              correlationId: props.correlationId,
            }),
          ),
          catchError((error: any) =>
            of(
              authApiActions.errorRemovingEmailFromProfile({
                error,
                statusCode: error?.statusCode ?? null,
                correlationId: props.correlationId,
              }),
            ),
          ),
        ),
      ),
    );
  });
  profileAddNewEmailVerificationEmailSent$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.sendAddNewEmailVerificationEmail),
      switchMap((props) =>
        this._accountService
          .sendProfileEmailUpdateVerificationEmail({ email: props.newEmail })
          .pipe(
            map((investigator) =>
              authApiActions.addNewEmailVerificationEmailSent(
                normalize(investigator, investigatorSchema),
              ),
            ),
            catchError((error: UpdateEmailErrorResponse | any) =>
              of(
                authApiActions.addNewEmailVerificationEmailSendFailed({
                  error,
                  errorCode: error?.changeEmailError ?? null,
                }),
              ),
            ),
          ),
      ),
    );
  });
  profileEmailUpdateVerified$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.verifyAddProfileEmail),
      switchMap((props) =>
        this._accountService
          .verifyAddProfileEmailChange({
            code: props.code,
            email: props.newEmail,
          })
          .pipe(
            map((profileUpdatedDto) =>
              authApiActions.profileEmailVerifiedAndAdded(profileUpdatedDto),
            ),
            tap((_) => this._router.navigate(['manage', 'profile'])),
            catchError((error: VerificationErrorResponse | any) =>
              of(
                authApiActions.profileEmailVerificationFailed({
                  error,
                  errorCode: error?.verificationErrorCode ?? null,
                }),
              ),
            ),
          ),
      ),
    );
  });

  passwordResetEmailSent$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.sendPasswordResetEmail),
      concatLatestFrom(() => this._store.select(selectAuthenticatedUser)),
      switchMap(([, user]) =>
        this._accountService.sendResetPasswordEmail().pipe(
          map((result) => {
            this._snackBar.open(
              user
                ? `A password reset email has been sent to ${user.email}`
                : throwErrorExp('No user found'),
              undefined,
              { duration: 5000 },
            );
            return authApiActions.passwordEmailResetSent();
          }),
          warnOnErrorWithRetryOption$(
            this._snackBar,
            'We were unable to send a password reset email',
            3000,
            undefined,
            undefined,
            true,
            true,
          ),
          catchError((error) =>
            of(authApiActions.passwordEmailResetFailed(error)),
          ),
        ),
      ),
    );
  });

  profileNameUpdated$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.updateProfileName),
      switchMap((props) =>
        this._accountService
          .updateProfileName({
            firstName: props.fullname.first,
            surname: props.fullname.surname,
            preferredName: props.fullname.preferred,
          })
          .pipe(
            map((profileUpdatedDto) => {
              return authApiActions.profileNameUpdated(profileUpdatedDto);
            }),
            warnOnErrorWithRetryOption$(
              this._snackBar,
              'An error occurred updating profile name',
              3000,
              undefined,
              undefined,
              true,
            ),
          ),
      ),
    );
  });

  primaryEmailUpdated$ = createEffect(() => {
      return this._actions$.pipe(
        ofType(authApiActions.updatePrimaryEmail),
        switchMap((props) =>
          this._accountService
            .updatePrimaryEmail({
              email: props.email,
            })
            .pipe(
              map((profileUpdatedDto) =>
                authApiActions.primaryEmailUpdated({
                  ...profileUpdatedDto,
                  correlationId: props.correlationId,
                }),
              ),
              catchError((error) => of(authApiActions.primaryEmailUpdateFailed({
                  error,
                  errorCode: error?.status ?? null,
                  correlationId: props.correlationId,
                })),
              ),
            ),
        ),
      );
    },
  );

  silentlyRefreshToken$ = createEffect(() => {
      return this._actions$.pipe(
        ofType(authApiActions.profileNameUpdated, authApiActions.profilePictureUpdated, authApiActions.primaryEmailUpdated),
        map(() => authApiActions.silentlyRefreshToken()),
      );
    },
  );

  connectAccount$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(authApiActions.connectSocialSignIn),
        // Social account linking is Auth0-specific
        filter(() => this._appConfig.config.authProvider === 'auth0'),
        switchMap((props) =>
          this._store.select(selectManagementToken).pipe(
            tap(
              (mt) =>
                !mt &&
                this._store.dispatch(authApiActions.getManagementAccessToken()),
            ),
            filter((t): t is string => !!t),
            take(1),
            switchMap((accessToken) =>
              from(this._authenticateSocialLogin(props.provider)).pipe(
                withLatestFrom(this._store.select(selectAuthenticatedUser)),
                switchMap(([targetUserIdToken, user]) =>
                  user
                    ? this._linkAccount(user, accessToken, targetUserIdToken)
                    : throwErrorExp('No user found'),
                ),
                switchMap((response) =>
                  this._accountService
                    .informAccountLink({ provider: props.provider })
                    .pipe(mapTo(of(response))),
                ),
                tap((response) => {
                  console.log('Account Link Response: ', response);
                  this._authProvider.loginWithRedirect({
                    authorizationParams: {
                      prompt: 'none',
                    },
                    appState: {
                      target: this._router.url,
                    },
                  });
                }),
              ),
            ),
          ),
        ),
      );
    },
    { dispatch: false },
  );

  disconnectAccount$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(authApiActions.disconnectSocialSignIn),
        // Social account unlinking is Auth0-specific
        filter(() => this._appConfig.config.authProvider === 'auth0'),
        switchMap((props) =>
          from(this._getAccessToken$).pipe(
            withLatestFrom(this._store.select(selectAuthenticatedUser)),
            filter<
              [string, IAuthenticatedUser | null],
              [string, IAuthenticatedUser]
            >((val): val is [string, IAuthenticatedUser] => !!val[1]),
            switchMap(([accessToken, user]) => {
              return this._unlinkAccount(user, props.provider, accessToken);
            }),
            tap((response) => {
              console.log('Unlink account response: ', response);
              this._authProvider.loginWithRedirect({
                authorizationParams: {
                  prompt: 'none',
                },
                appState: {
                  target: this._router.url,
                },
              });
            }),
          ),
        ),
      );
    },
    { dispatch: false },
  );
  managementAccessToken$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.getManagementAccessToken),
      // Management API token is Auth0-specific
      filter(() => this._appConfig.config.authProvider === 'auth0'),
      switchMap((_) =>
        this._authProvider
          .getAccessTokenSilently({
            authorizationParams: {
              audience: this._appConfig.config.auth0ManagementApiAudience,
              scope:
                'openid email profile read:current_user update:current_user_identities',
            },
          })
          .pipe(
            catchError((error) =>
              error.error === 'login_required' && this._authProvider.getAccessTokenWithPopup
                ? this._authProvider.getAccessTokenWithPopup({
                  authorizationParams: {
                    audience:
                    this._appConfig.config.auth0ManagementApiAudience,
                    scope:
                      'openid email profile read:current_user update:current_user_identities',
                  },
                })
                : throwError(error),
            ),
            map((token) =>
              token
                ? authApiActions.managementAccessTokenReceived({ token })
                : throwErrorExp('No token returned from auth provider'),
            ),
            catchError((error) =>
              of(authApiActions.managementAccessTokenFailed({ error })),
            ),
          ),
      ),
    );
  });

  signInActions$: Observable<Action | void> = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(authApiActions.startSignIn),
        switchMap((props) =>
          this._authProvider.loginWithRedirect({
            authorizationParams: {
              prompt: props.prompt ?? undefined,
              connection: props.connection,
              linkSocial: props.linkSocial,
              socialEmail: props.socialEmail,
              login_hint: props.login_hint,
              screen_hint: props.screen_hint,
            },
            appState: {
              target: props.redirectUrl
                ? props.redirectUrl
                : this._router.url,
            },
          }).pipe(
            catchError((result) => {
              console.debug('silent refresh failed with:', result);
              return of(
                authApiActions.authenticationError({
                  message: `silent sign in failed with error ${
                    result && result.reason && result.reason.error
                  }`,
                  object: result,
                  handled: false,
                }),
              );
            }),
          ),
        ),
      );
    },
    { dispatch: false },
  );

  authError$ = createEffect(() => {
    return this._authProvider.error$.pipe(
      tap((error: any) =>
        error.error === 'login_required'
          ? null
          : this._snackBar.open(error.message, 'DISMISS'),
      ),
      map((err) =>
        authApiActions.authenticationError({
          message: err.message,
          object: err,
          handled: true,
        }),
      ),
    );
  });

  refreshTokenSilentlyRefreshed$ = createEffect(() => {
      return this._actions$.pipe(
        ofType(authApiActions.silentlyRefreshToken),
        switchMap((_) => this._authProvider.getAccessTokenSilently({ cacheMode: 'off' })),
        tap((token) => console.warn('token refreshed from silentRefresh effect', token)),
      );
    },
    { dispatch: false },
  );

  userRegistered$: Observable<Action | void> = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(authApiActions.startRegistrationRedirect),
        switchMap((props) =>
          this._authProvider
            .loginWithRedirect({
              authorizationParams: {
                screen_hint: 'signup',
              },
              appState: {
                target: props.redirectUrl
                  ? props.redirectUrl
                  : this._router.url,
              },
            })
            .pipe(
              catchError((result) => {
                console.debug('silent refresh failed with:', result);
                return of(
                  authApiActions.authenticationError({
                    message: `silent sign in failed with error ${
                      result && result.reason && result.reason.error
                    }`,
                    object: result,
                    handled: false,
                  }),
                );
              }),
            ),
        ),
      );
    },
    { dispatch: false },
  );

  signOut$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(authApiActions.signOut),
        tap((props) =>
          this._authProvider.logout({
            logoutParams: {
              localOnly: props.onlyLocal,
              returnTo:
                props.noRedirect === undefined || !props.noRedirect
                  ? `${location.protocol}//${location.host}`
                  : undefined,
            },
          }),
        ),
      );
    },
    { dispatch: false },
  );

  silentSignOut$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(authApiActions.silentSignOut),
        filter(() => this._appConfig.config.authProvider === 'auth0'),
        tap(() => {
          const domain = this._appConfig.config.auth0CustomDomain;
          const clientId = this._appConfig.config.auth0ClientId;
          const i = document.createElement('iframe');
          i.style.display = 'none';
          i.onload = function() {
            i.parentNode!.removeChild(i);
          };
          i.src = `https://${domain}/v2/logout?client_id=${clientId}`;
          document.body.appendChild(i);
        }),
      );
    },
    { dispatch: false },
  );

  getUser$: Observable<Action> = createEffect(() => {
    return this._authProvider.idTokenClaims$.pipe(
      distinctUntilChanged(
        (a, b) => JSON.stringify(a) === JSON.stringify(b),
      ),
      tap((beforeFilter) => {
        console.warn('before', beforeFilter);
      }),
      filter((user: any | null | undefined) => !!user),
      tap((user: any) => console.log('user loaded with claims', user)),
      map((idTokenClaims: IdToken) => ({
        idToken: idTokenClaims.__raw,
        user: createUserFromClaims(<any>idTokenClaims),
        claims: idTokenClaims,
      })),
      map(({ idToken, user }) => {
        const storedImpersonationString = localStorage.getItem(
          `${localStorageImpersonationKeyPrefix}${user.id}`,
        );
        let impersonating: IUser | null = null;
        if (storedImpersonationString) {
          const storedImpersonationInfo = <IStoredImpersonationInfo>(
            JSON.parse(storedImpersonationString)
          );
          impersonating = storedImpersonationInfo.impersonatedUser;
        }
        return authApiActions.idTokenReceived({ user, impersonating, idToken });
      }),
    );
  });

  loadingUser$ = createEffect(() => {
    return this._authProvider.isLoading$.pipe(
      map((loading) => {
        return loading
          ? authApiActions.loadingUser()
          : authApiActions.loadingUserComplete();
      }),
    );
  });

  signInRecorded$: Observable<Action> = createEffect(() => {
    return this._authProvider.user$.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      filter(
        (user: User | null | undefined) =>
          (!!user && (user.sub?.startsWith('auth0') || this._appConfig.config.authProvider === 'oidc' || this._appConfig.config.authProvider === 'bff')) ?? throwErrorExp('no user'),
      ),
      tap((_) => console.warn('ABOUT TO RECORD SIGN-IN')),
      switchMap((_: User) => {
        return this._applicationService.recordSignin().pipe(
          warnOnErrorWithRetryOption$(
            this._snackBar,
            'There was a problem contacting the server',
            4000,
          ),
          map((usage) =>
            authApiActions.signInRecorded({
              ...normalize(usage, investigatorInitialInfoDtoSchema),
              correlationId: uuid(),
            }),
          ),
        );
      }),
    );
  });

  saveImpersonationInLocalStorage$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(authApiActions.startUserImpersonation),
        switchMap((props) =>
          this._store.select(selectAuthenticatedUserId).pipe(
            filter((auid) => !!auid),
            tap((authenticatedUserId) => {
              localStorage.setItem(
                `${localStorageImpersonationKeyPrefix}${authenticatedUserId}`,
                JSON.stringify({
                  impersonatedUser: props.userToImpersonate,
                  authenticatedUserId,
                }),
              );
            }),
          ),
        ),
      );
    },
    { dispatch: false },
  );

  removeImpersonationFromLocalStorage$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(authApiActions.stopUserImpersonation),
        switchMap(() =>
          this._store.select(selectAuthenticatedUserId).pipe(
            tap((auId) => {
              localStorage.removeItem(
                `${localStorageImpersonationKeyPrefix}${auId}`,
              );
            }),
          ),
        ),
      );
    },
    { dispatch: false },
  );

  signupUser$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.startSignUp),
      switchMap((props) =>
        this._accountService
          .signUp(props)
          .pipe(map((payload) => authApiActions.userSignUpSucceeded(payload))),
      ),
    );
  });
  private _getAccessToken$ = this._store.select(selectManagementToken).pipe(
    tap((t) => {
      if (!t) {
        this._store.dispatch(authApiActions.getManagementAccessToken());
      }
    }),
    filter((t): t is string => !!t),
    take(1),
  );

  constructor(
    private _actions$: Actions,
    private _applicationService: ApplicationHttpService,
    private _snackBar: MatSnackBar,
    private _store: Store,
    private _router: Router,
    private _applicationRef: ApplicationRef,
    @Inject(AUTH_PROVIDER) private _authProvider: IAuthProvider,
    private _appConfig: AppConfigService,
    private _accountService: AccountHttpService,
    private _httpClient: HttpClient,
    private _cookieService: CookieService,
  ) {
  }

  private _linkAccount(
    user: IAuthenticatedUser,
    token: string,
    targetUserIdToken: string,
  ) {
    console.log('la');
    return this._httpClient.post(
      `${this._appConfig.config.auth0ManagementCustomApiRoot}/users/${user.sub}/identities`,
      {
        link_with: targetUserIdToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );
  }

  private _unlinkAccount(
    user: IAuthenticatedUser,
    provider: string,
    accessToken: string,
  ) {
    const user_id = user.identities.find(
      (id) => id.provider === provider,
    )?.user_id;
    if (!user_id) throw Error('matching provider cannot be found');
    return this._httpClient.delete(
      `${this._appConfig.config.auth0ManagementCustomApiRoot}/users/${user.sub}/identities/${provider}/${user_id}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
  }

  /** Auth0-specific: social login via popup for account linking. */
  private async _authenticateSocialLogin(
    providerName: string,
  ): Promise<string> {
    console.log('ac');
    const auth0Client = await createAuth0Client({
      domain: this._appConfig.config.auth0CustomDomain,
      clientId: this._appConfig.config.auth0ClientId,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
    });
    console.log('ac2');
    await auth0Client.loginWithPopup({
      authorizationParams: {
        connection: providerName,
        accountLink: 'true',
        max_age: 0,
        scope: 'openid',
      },
    });
    const idTokenClaims = await auth0Client.getIdTokenClaims();
    if (!idTokenClaims) throw Error('Id token could not be retrieved');
    console.log('ac3');
    return idTokenClaims.__raw;
  }
}
